import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { CheckCircle2, ExternalLink, Eye, UserPlus } from 'lucide-react';
import EmptyCell from '../../../components/EmptyCell';
import { Skeleton } from '../../../components/ui/skeleton';
import { getJiraIssueUrl } from '../../../utils/jiraUrls';
import type { AdminIssue } from '../../../api/adminIssuesApi';

const formatDate = (value: string): string => (value ? dayjs(value).format('DD/MM/YYYY') : '—');

export const statusCategoryClass: Record<AdminIssue['statusCategory'], string> = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  indeterminate: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  done: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
};

/**
 * The « ask somebody else » action, or `undefined` on a listing that offers none.
 *
 * The rule lives on the page, not here: whether a row may be reopened depends on the desk's
 * workflow, on the caller's rights and on what Postgres knows about the mission — none of which a
 * column should be reading. This only draws the button the page allows.
 */
export interface ReassignAction {
  canReassign: (issue: AdminIssue) => boolean;
  onReassign: (issue: AdminIssue) => void;
}

/**
 * The « Parent » column, shared by both tabs. The key filters the listing on that parent; the icon
 * beside it opens the parent in Jira — one target each. Not sortable: Jira cannot order on a link.
 */
export const parentKeyColumn = <T extends Pick<AdminIssue, 'parentKey'>,>(
  t: TFunction,
  onFilter: (key: string) => void,
  jiraBrowseBase?: string | null
): ColumnDef<T> => ({
  id: 'parentKey',
  header: t('Parent'),
  accessorFn: (issue) => issue.parentKey ?? '',
  enableSorting: false,
  // A `table-fixed` table gives no room to a column without a width: it would overlap the next one.
  meta: { width: 150 },
  cell: ({ row }) => {
    const key = row.original.parentKey;
    if (!key) return <EmptyCell />;
    return (
      <span className="inline-flex items-center gap-1 font-mono text-[13px]">
        <button
          type="button"
          onClick={() => onFilter(key)}
          aria-label={t('Filter by parent {{key}}', { key })}
          title={t('Filter by parent {{key}}', { key })}
          className="text-primary hover:underline"
        >
          {key}
        </button>
        <a
          href={getJiraIssueUrl(key, jiraBrowseBase)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('Open {{key}} in Jira', { key })}
          title={t('Open {{key}} in Jira', { key })}
          className="rounded p-0.5 text-muted-foreground hover:text-primary"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      </span>
    );
  },
});

// `jiraBrowseBase` comes from AppContext (backend JSM_DOMAIN): without it the links
// fall back to the production instance, which is wrong on preprod/sandbox.
export const getIssuesColumns = (
  t: TFunction,
  onValidate: (issue: AdminIssue) => void,
  onView: (issue: AdminIssue) => void,
  jiraBrowseBase?: string | null,
  reassign?: ReassignAction,
  // The store names land after the listing: the cell greys out instead of reading « — »,
  // which would say « no store » about a ticket that has one.
  storeLabelsLoading = false,
  parent?: { onFilter: (key: string) => void }
): ColumnDef<AdminIssue>[] => [
  {
    accessorKey: 'issueKey',
    header: t('Key'),
    cell: ({ row }) => (
      <a
        href={getJiraIssueUrl(row.original.issueKey, jiraBrowseBase)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-mono text-sm text-primary hover:underline"
      >
        {row.original.issueKey}
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    ),
  },
  ...(parent ? [parentKeyColumn<AdminIssue>(t, parent.onFilter, jiraBrowseBase)] : []),
  {
    accessorKey: 'summary',
    header: t('Summary'),
    cell: ({ row }) => (
      <span className="block max-w-md truncate" title={row.original.summary}>
        {row.original.summary}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: t('Status'),
    cell: ({ row }) => (
      // Single line, cropped with an ellipsis: long workflow names ("EN ATTENTE CREATION
      // ISSUES FILLES") must not wrap the badge over several lines. Full name on hover.
      <span
        title={row.original.status}
        className={`inline-block max-w-[200px] truncate align-middle px-2 py-0.5 rounded-full text-xs font-medium ${
          statusCategoryClass[row.original.statusCategory]
        }`}
      >
        {row.original.status || '—'}
      </span>
    ),
  },
  {
    accessorKey: 'requestType',
    header: t('Type'),
    cell: ({ row }) => <span className="text-sm">{row.original.requestType ?? '—'}</span>,
  },
  {
    id: 'store',
    header: t('Store'),
    accessorFn: (issue) => issue.store ?? '',
    cell: ({ row }) => {
      const { store, storeObjectId } = row.original;
      if (!store && storeObjectId && storeLabelsLoading) {
        return <Skeleton className="h-4 w-28" />;
      }
      return (
        <span className="block max-w-[200px] truncate text-sm" title={store ?? ''}>
          {store ?? '—'}
        </span>
      );
    },
  },
  // A column with only an `id` has no accessor, so `row.getValue(id)` — what the CSV export
  // reads — comes back undefined. `accessorFn` is what puts these in the export.
  {
    id: 'reporter',
    header: t('Reporter'),
    accessorFn: (issue) => issue.reporter?.displayName ?? '',
    cell: ({ row }) => (
      <span className="text-sm">{row.original.reporter?.displayName ?? '—'}</span>
    ),
  },
  {
    id: 'assignee',
    header: t('Assignee'),
    accessorFn: (issue) => issue.assignee?.displayName ?? '',
    cell: ({ row }) => (
      <span className="text-sm">{row.original.assignee?.displayName ?? '—'}</span>
    ),
  },
  {
    accessorKey: 'created',
    header: t('Created'),
    cell: ({ row }) => <span className="text-sm text-gray-600">{formatDate(row.original.created)}</span>,
  },
  {
    accessorKey: 'updated',
    header: t('Updated'),
    cell: ({ row }) => <span className="text-sm text-gray-600">{formatDate(row.original.updated)}</span>,
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-1">
        {row.original.canValidate && (
          <button
            type="button"
            onClick={() => onValidate(row.original)}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-primary hover:bg-primary/10"
            title={t('Validate')}
          >
            <CheckCircle2 className="w-4 h-4" />
            {t('Validate')}
          </button>
        )}
        {/* Before the eye: a mission already scheduled has no other door to the assignment
            panel, and reading the ticket is not what an operator comes here to do (C1). */}
        {reassign?.canReassign(row.original) && (
          <button
            type="button"
            onClick={() => reassign.onReassign(row.original)}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-primary hover:bg-primary/10"
          >
            <UserPlus className="w-4 h-4" />
            {t('Ask another technician')}
          </button>
        )}
        <button
          type="button"
          onClick={() => onView(row.original)}
          className="inline-flex items-center rounded p-1.5 text-primary hover:bg-primary/10"
          title={t('View')}
          aria-label={t('View')}
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>
    ),
  },
];
